/**
 * Settings API — v4 client
 * canonical: S4 — lines 2014–2229
 *
 * Endpoints covered:
 *   GET  /api/settings             → CompanySettings
 *   POST /api/settings             → CompanySettings (upsert)
 *   POST /api/settings/logo        → multipart image upload
 *   GET  /api/settings/logo        → image binary (auth-gated; use fetchLogoObjectUrl)
 *   POST /api/settings/signature   → multipart image upload
 *   GET  /api/settings/signature   → image binary (auth-gated; use fetchSignatureObjectUrl)
 *   GET  /api/stripe/account-status
 *   POST /api/stripe/connect       → { url, account_id }
 *   GET  /api/quickbooks/status
 *   DELETE /api/auth/account
 */

import { apiFetch, getToken } from './client'

// ── Types ──────────────────────────────────────────────────────────────────

export interface CompanySettings {
  // Company profile
  company_name:          string | null
  company_address:       string | null
  company_city:          string | null
  company_state:         string | null
  company_zip:           string | null
  license_number:        string | null
  // Contact
  contact_name:          string | null
  contact_phone:         string | null
  contact_email:         string | null
  // Logo & signature stored as filenames on disk — use fetchLogoObjectUrl/fetchSignatureObjectUrl
  logo_filename:         string | null
  logo_original_name:    string | null
  signature_filename:    string | null
  // Project defaults
  default_payment_terms: string | null
  default_retainage:     number | null
  auto_prelien_ca:       boolean | null
  auto_attach_waivers:   boolean | null
  // Cash forecast inputs (cents)
  weekly_payroll_cents?: number | null
  cash_on_hand_cents?:   number | null
  cash_on_hand_as_of?:   string | null
  monthly_overhead_cents?: number | null
  // Job numbering
  job_number_format:     string | null
  // Reminders
  reminder_7before:      boolean | null
  reminder_due:          boolean | null
  reminder_7after:       boolean | null
  reminder_retention:    boolean | null
  credit_card_enabled:   boolean | null
  updated_at:            string | null
  aria_preferences?:     AriaPreferences | null
  aria_training_counts?: AriaTrainingCounts | null
  aria_billing?:         AriaBillingStatus | null
}

export type ActionMode = 'ask' | 'suggest' | 'auto'

export interface AriaVoicePrefs {
  voice_on_default: boolean
  read_aloud:         boolean
}

export interface AriaPreferences {
  action_mode:             ActionMode
  confirmation_threshold:  number
  soren_enabled:           boolean
  is_aria_pro?:            boolean
  brief_enabled?:          boolean
  brief_days?:             ('tuesday' | 'friday')[]
  show_test_projects?:     boolean
  voice_prefs:             AriaVoicePrefs
}

export interface AriaBillingStatus {
  mode:                      'byo' | 'app' | 'none'
  is_pro:                    boolean
  included_monthly:          number
  used_monthly:              number
  remaining_monthly:         number | null
  daily_cap:                 number
  daily_used:                number
  claude_oauth_configured:   boolean
}

export interface AriaClaudeConnectConfig {
  oauth_enabled:     boolean
  billing:           AriaBillingStatus
  authorize_url:     string | null
  waitlist_message:  string | null
}

export interface AriaTrainingCounts {
  contracts:           number
  pay_apps:            number
  approved_invoices:   number
  rejected_invoices:   number
}

export interface StripeStatus {
  connected:        boolean
  charges_enabled?: boolean
  payouts_enabled?: boolean
  account_id?:      string
  business_name?:   string
  status?:          'active' | 'pending'
}

export interface QBStatus {
  connected:     boolean
  needs_reconnect?: boolean
  company_name?: string
  last_sync?:    string
  last_sync_at?: string
  sandbox?:      boolean
  environment?:  string
  qb_auto_sync?: boolean
  last_error?:   string | null
}

// ── API Functions ──────────────────────────────────────────────────────────

/** GET /api/aria/claude/connect-config — OAuth availability + billing snapshot */
export async function loadAriaClaudeConnectConfig(): Promise<AriaClaudeConnectConfig> {
  const res = await apiFetch<{ data: AriaClaudeConnectConfig }>('GET', '/api/aria/claude/connect-config')
  return res.data
}

/** GET /api/settings → full company_settings row (empty object if no row yet) */
export async function loadSettings(): Promise<CompanySettings> {
  return apiFetch<CompanySettings>('GET', '/api/settings')
}

/** POST /api/settings → upsert and return updated row */
export async function saveSettings(data: Partial<CompanySettings>): Promise<CompanySettings> {
  return apiFetch<CompanySettings>('POST', '/api/settings', data)
}

/** POST /api/settings/logo — multipart file upload */
export async function uploadLogo(file: File): Promise<CompanySettings> {
  const token = getToken()
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/settings/logo`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<CompanySettings>
}

/** POST /api/settings/signature — multipart file upload */
export async function uploadSignature(file: File): Promise<CompanySettings> {
  const token = getToken()
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/settings/signature`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<CompanySettings>
}

/**
 * Fetch logo as a blob object URL (required because GET /api/settings/logo is auth-gated).
 * Returns null if no logo is set (404) or on any error.
 * IMPORTANT: caller must call URL.revokeObjectURL(url) on unmount to avoid memory leaks.
 */
export async function fetchLogoObjectUrl(): Promise<string | null> {
  const token = getToken()
  try {
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/settings/logo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (res.status === 404) return null
    if (!res.ok) return null
    return URL.createObjectURL(await res.blob())
  } catch { return null }
}

/**
 * Fetch signature as a blob object URL.
 * Returns null if no signature is set.
 * IMPORTANT: caller must revoke on unmount.
 */
export async function fetchSignatureObjectUrl(): Promise<string | null> {
  const token = getToken()
  try {
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/settings/signature`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (res.status === 404) return null
    if (!res.ok) return null
    return URL.createObjectURL(await res.blob())
  } catch { return null }
}

/** GET /api/stripe/account-status */
export async function loadStripeStatus(): Promise<StripeStatus> {
  return apiFetch<StripeStatus>('GET', '/api/stripe/account-status')
}

/**
 * POST /api/stripe/connect → { url, account_id }
 * Caller MUST do: window.location.href = data.url
 */
export async function stripeConnect(): Promise<{ url: string; account_id: string }> {
  return apiFetch<{ url: string; account_id: string }>('POST', '/api/stripe/connect')
}

/** POST /api/stripe/dashboard-link → { url } */
export async function getStripeDashboardLink(): Promise<{ url: string }> {
  return apiFetch<{ url: string }>('POST', '/api/stripe/dashboard-link')
}

/** DELETE /api/stripe/connect */
export async function disconnectStripe(): Promise<{ ok: boolean }> {
  await apiFetch<{ message: string }>('DELETE', '/api/stripe/connect')
  return { ok: true }
}

/** GET /api/quickbooks/status */
export async function loadQBStatus(): Promise<QBStatus> {
  return apiFetch<QBStatus>('GET', '/api/quickbooks/status')
}

/** DELETE /api/auth/account — permanently deletes account + all cascade data */
export async function deleteAccount(): Promise<void> {
  return apiFetch<void>('DELETE', '/api/auth/account')
}

/** POST /api/account/export — email 24h download link (1/day) */
export async function requestDataExport(): Promise<string> {
  const res = await apiFetch<{ message?: string }>('POST', '/api/account/export')
  return res?.message ?? 'Check your email for the download link'
}
